/**
 * The playing grid: square cells covering the whole viewport.
 *
 * One cell is about 1.05% of the viewport width (≈20px on 1920px), clamped to
 * 10–28px, which makes the word about a third of the screen wide like the
 * design. Long words and small screens shrink the cells until the word fits.
 * The CSS twin of this rule is --snake-cell in 40_game.sass.
 */
const CELL_RATIO = 0.0105
const MIN_CELL = 10
const MAX_CELL = 28
const TINY_CELL = 4
const WORD_MAX_WIDTH = 0.92
const WORD_MAX_HEIGHT = 0.8

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

/**
 * @param {{width: number, height: number, safeTop: number, safeBottom: number}} view
 * @param {number} cell
 */
export function gridFor(view, cell) {
  const cols = Math.max(1, Math.floor(view.width / cell))
  const rows = Math.max(1, Math.floor(view.height / cell))
  const offsetX = Math.floor((view.width - cols * cell) / 2)
  const offsetY = Math.floor((view.height - rows * cell) / 2)

  // Rows fully clear of the header and footer bar — where new food appears.
  const safeRowStart = clamp(Math.ceil((view.safeTop - offsetY) / cell), 0, rows - 1)
  const safeRowEnd = clamp(
    Math.floor((view.height - view.safeBottom - offsetY) / cell),
    safeRowStart + 1,
    rows
  )

  return { cell, cols, rows, offsetX, offsetY, safeRowStart, safeRowEnd }
}

/**
 * @param {{width: number, height: number, safeTop: number, safeBottom: number}} view
 * @param {{cols: number, rows: number}} word
 */
export function layoutFor(view, word) {
  let cell = clamp(Math.round(view.width * CELL_RATIO), MIN_CELL, MAX_CELL)

  if (word.cols) {
    cell = Math.min(cell, Math.floor((view.width * WORD_MAX_WIDTH) / (word.cols + 2)))
  }
  if (word.rows) {
    const safeHeight = Math.max(0, view.height - view.safeTop - view.safeBottom)
    cell = Math.min(cell, Math.floor((safeHeight * WORD_MAX_HEIGHT) / (word.rows + 2)))
  }

  return gridFor(view, Math.max(TINY_CELL, cell))
}

/** The word's cells centred on the grid. */
export function placeWord(grid, word) {
  const left = Math.floor((grid.cols - word.cols) / 2)
  const top = Math.floor((grid.rows - word.rows) / 2)

  return word.cells
    .map(([x, y]) => ({ x: x + left, y: y + top }))
    .filter(({ x, y }) => x >= 0 && y >= 0 && x < grid.cols && y < grid.rows)
}
